import type { QueryHookOptions } from '@apollo/client';
import { gql, useQuery } from '@apollo/client';
import _ from 'lodash';
import { useMemo } from 'react';
import type {
  AllPostsQuery,
  AllPostsQueryVariables,
} from '../../graphql/generated/graphqlTypes';
import { useAllTopicMap } from '../../topic/useAllTopics';
import updateQueryList from '../../utils/graphql/updateQueryList';
import populatePostsWithFullTopics from '../../post/populatePostsWithFullTopics';
import PostIcon from '../../post/PostIcon';
import PostTitleWithIcons from '../../post/PostTitleWithIcons';

// Must keep relation fields in sync with PostResolver.all/2
/* eslint-disable slab-graphql/no-unused-colocated-fragments */
export const USE_ALL_POSTS_FOR_ENTITY_SEARCH_POST_FRAGMENT = gql`
  fragment useAllPostsForEntitySearch_post on SlimPostFields {
    id
    archivedAt
    publishedAt
    title
    series {
      id
    }
    ...PostIcon_post
    ...populatePostsWithFullTopics_posts
    ...PostTitleWithIcons_post
  }
  ${PostIcon.fragments.post}
  ${populatePostsWithFullTopics.fragments.posts}
  ${PostTitleWithIcons.fragments.post}
`;
/* eslint-enable */

const ALL_POSTS_QUERY = gql`
  query allPosts {
    session: currentSession {
      organization {
        id
        posts {
          ...useAllPostsForEntitySearch_post
        }
      }
    }
  }
  ${USE_ALL_POSTS_FOR_ENTITY_SEARCH_POST_FRAGMENT}
`;

const useAllPostsForEntitySearch = (
  options: QueryHookOptions<AllPostsQuery, AllPostsQueryVariables> = {},
) => {
  const { data } = useQuery<AllPostsQuery, AllPostsQueryVariables>(
    ALL_POSTS_QUERY,
    options,
  );

  const topicMap = useAllTopicMap();

  return useMemo(() => {
    const basePosts = data?.session?.organization?.posts || [];
    const withFullTopics = populatePostsWithFullTopics(basePosts, topicMap);
    const withoutTypenames = withFullTopics.map(
      ({ __typename, externalPost, series, ...post }) => ({
        ...post,
        externalPost: externalPost && _.omit(externalPost, '__typename'),
        series: series && _.omit(series, '__typename'),
      }),
    );

    return withoutTypenames;
  }, [data, topicMap]);
};

export const useAllPostMapForEntitySearch = (
  ...args: Parameters<typeof useAllPostsForEntitySearch>
) => {
  const posts = useAllPostsForEntitySearch(...args);
  const postMap = useMemo(() => _.keyBy(posts, 'id'), [posts]);

  return postMap;
};

export const getMutationAllPostsUpdater = (dataKey: string) =>
  updateQueryList(
    { query: ALL_POSTS_QUERY },
    'session.organization.posts',
    dataKey,
  );

export default useAllPostsForEntitySearch;
